import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  Text,
} from "@chakra-ui/react";
import {
  CONDICOES_SEGURO_CONCILIACAO,
  CONDICOES_SEGURO_LABEL,
  FonteConciliacao,
  FONTES_CONCILIACAO,
  FONTES_LABEL,
  OPERADORES_CONCILIACAO,
  OPERADORES_LABEL,
  CondicaoSeguroConciliacao,
} from "@/models/conciliation-config.model";
import { CAMPOS_POR_FONTE_CONCILIACAO } from "./camposPorFonteConciliacao";
import { RegraConciliacaoCardProps } from "./types";

const getCamposByFonte = (fonte: FonteConciliacao | ""): string[] => {
  if (!fonte)
    return [];

  return CAMPOS_POR_FONTE_CONCILIACAO[fonte] ?? [];
};

export function RegraConciliacaoCard({
  regra,
  index,
  totalRegras,
  onRemoveRegra,
  onUpdateRegra,
  onUpdateRegraOrigem,
  onUpdateRegraDestino,
}: RegraConciliacaoCardProps) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={3}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontWeight="bold">Regra {index + 1}</Text>
        <IconButton
          aria-label="remover-regra"
          size="sm"
          colorScheme="red"
          icon={<DeleteIcon />}
          onClick={() => onRemoveRegra(index)}
          isDisabled={totalRegras === 1}
        />
      </Flex>

      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={3}>
        <FormControl isRequired>
          <FormLabel>Origem - Fonte</FormLabel>
          <Select
            value={regra.origem.fonte}
            onChange={(event) =>
              onUpdateRegraOrigem(index, {
                fonte: event.target.value as FonteConciliacao | "",
                campo: "",
              })
            }
          >
            <option value="">Selecione</option>
            {FONTES_CONCILIACAO.map((fonte) => (
              <option key={fonte} value={fonte}>
                {FONTES_LABEL[fonte]}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Origem - Campo</FormLabel>
          <Select
            value={regra.origem.campo}
            isDisabled={!regra.origem.fonte}
            onChange={(event) =>
              onUpdateRegraOrigem(index, {
                campo: event.target.value,
              })
            }
          >
            <option value="">Selecione</option>
            {getCamposByFonte(regra.origem.fonte).map((campo) => (
              <option key={campo} value={campo}>
                {campo}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Origem - Rótulo</FormLabel>
          <Input
            value={regra.origem.rotulo || ""}
            onChange={(event) =>
              onUpdateRegraOrigem(index, {
                rotulo: event.target.value,
              })
            }
          />
        </FormControl>
      </Flex>

      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={3}>
        <FormControl isRequired>
          <FormLabel>Destino - Fonte</FormLabel>
          <Select
            value={regra.destino.fonte}
            onChange={(event) =>
              onUpdateRegraDestino(index, {
                fonte: event.target.value as FonteConciliacao | "",
                campo: "",
              })
            }
          >
            <option value="">Selecione</option>
            {FONTES_CONCILIACAO.map((fonte) => (
              <option key={fonte} value={fonte}>
                {FONTES_LABEL[fonte]}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Destino - Campo</FormLabel>
          <Select
            value={regra.destino.campo}
            isDisabled={!regra.destino.fonte}
            onChange={(event) =>
              onUpdateRegraDestino(index, {
                campo: event.target.value,
              })
            }
          >
            <option value="">Selecione</option>
            {getCamposByFonte(regra.destino.fonte).map((campo) => (
              <option key={campo} value={campo}>
                {campo}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Destino - Rótulo</FormLabel>
          <Input
            value={regra.destino.rotulo || ""}
            onChange={(event) =>
              onUpdateRegraDestino(index, {
                rotulo: event.target.value,
              })
            }
          />
        </FormControl>
      </Flex>

      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={3}>
        <FormControl isRequired>
          <FormLabel>Operador</FormLabel>
          <Select
            value={regra.operador}
            onChange={(event) =>
              onUpdateRegra(index, {
                operador: event.target.value as typeof regra.operador,
              })
            }
          >
            {OPERADORES_CONCILIACAO.map((operador) => (
              <option key={operador} value={operador}>
                {OPERADORES_LABEL[operador]}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Tolerância</FormLabel>
          <Input
            type="number"
            placeholder="0"
            value={regra.tolerancia || ""}
            onChange={(event) =>
              onUpdateRegra(index, {
                tolerancia: Number(event.target.value),
              })
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>Descrição</FormLabel>
          <Input
            value={regra.descricao || ""}
            onChange={(event) =>
              onUpdateRegra(index, {
                descricao: event.target.value,
              })
            }
          />
        </FormControl>
      </Flex>

      <FormLabel>Condição de seguro da regra</FormLabel>
      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={2} align="center">
        <Box
          as="label"
          display="inline-flex"
          alignItems="center"
          whiteSpace="nowrap"
          gap={2}
        >
          <input
            type="checkbox"
            checked={Boolean(regra.obrigatoria)}
            onChange={(event) =>
              onUpdateRegra(index, {
                obrigatoria: event.target.checked,
              })
            }
          />
          Regra obrigatória
        </Box>

        <FormControl>
          <Select
            value={regra.condicaoSeguro ?? "QUALQUER"}
            onChange={(event) =>
              onUpdateRegra(index, {
                condicaoSeguro: event.target.value as CondicaoSeguroConciliacao,
              })
            }
          >
            {CONDICOES_SEGURO_CONCILIACAO.map((condicao) => (
              <option key={condicao} value={condicao}>
                {CONDICOES_SEGURO_LABEL[condicao]}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>
    </Box>
  );
}